import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDraftInvoiceQuery } from '@/api/queries';
import type { ErrorResponse } from '@/api/handlers/responses';
import { PATH } from '@/router/PATH';
import {
    draftToFormState,
    type ManualImportFormState,
} from './manualImportMeta';

/**
 * Resume/edit companion for `useManualImport`. Resolves the draft id from the
 * URL — the `/purchases/manual-import/:draftId` route param (resume from the
 * drafts table) or the `?draftId=` search param (a fresh draft whose first
 * save lifted the POSTed id into the URL) — fetches the saved draft, and maps
 * it into the `initialDraft` seed.
 *
 * The id is captured **once per mount**: hydration only happens when the page
 * opens on an existing draft (resume or reload). When the first save assigns
 * an id mid-session, the form already holds newer state than the server, so
 * no GET fires and nothing remounts — `onDraftCreated` only rewrites the URL
 * (survives the dev server's full-page live reload).
 */
export const useManualImportDraft = () => {
    const params = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    // Captured at mount — see the docblock.
    const [draftId] = React.useState<string | null>(
        () => params.draftId ?? searchParams.get('draftId'),
    );

    const draftQuery = useDraftInvoiceQuery(draftId);

    const initialDraft = React.useMemo<ManualImportFormState | undefined>(
        () =>
            draftQuery.data?.success
                ? draftToFormState(draftQuery.data.response)
                : undefined,
        [draftQuery.data],
    );

    // Gate the error while a retry is in flight so Retry doesn't re-surface
    // the stale failed envelope (same pattern as the widget hooks).
    const hydrateError = React.useMemo<ErrorResponse | null>(
        () =>
            !draftQuery.isFetching &&
            draftQuery.data &&
            !draftQuery.data.success
                ? (draftQuery.data.error ?? null)
                : null,
        [draftQuery.isFetching, draftQuery.data],
    );

    const onRetry = React.useCallback(() => {
        void draftQuery.refetch();
    }, [draftQuery]);

    const onDraftCreated = React.useCallback(
        (createdId: string) =>
            setSearchParams({ draftId: createdId }, { replace: true }),
        [setSearchParams],
    );

    const onDraftDiscarded = React.useCallback(
        () => navigate(PATH.PURCHASES.ROOT),
        [navigate],
    );

    const onImported = React.useCallback(
        () => navigate(PATH.PURCHASES.ROOT),
        [navigate],
    );

    return {
        draftId,
        initialDraft,
        // Hydrating = an id is in the URL but the saved draft isn't here yet.
        isHydrating: draftId !== null && initialDraft === undefined,
        hydrateError,
        onRetry,
        onDraftCreated,
        onDraftDiscarded,
        onImported,
    } as const;
};
